import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  AlertCircle, ArrowLeft, ChevronLeft, ChevronRight, Download, Flame, Globe, KeyRound, Loader2,
  Clock3, LogOut, Mail, MessageCircle, Phone, RefreshCw, RotateCcw, Search, ShieldCheck, Sparkles, Trash2, Users, X
} from "lucide-react";
import ReportDashboard from "../components/ReportDashboard";
import { mapAIReport, formatINR } from "../components/ReportMapper";

const TOKEN_KEY = "emeis-admin-token";

const getToken = () => { try { return localStorage.getItem(TOKEN_KEY); } catch { return null; } };
const setToken = (t) => { try { t ? localStorage.setItem(TOKEN_KEY, t) : localStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ } };

const adminApi = axios.create({ baseURL: `${process.env.REACT_APP_BE_BASE_URL}/admin` });
adminApi.interceptors.request.use(cfg => {
  const t = getToken();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const errMsg = (err, fallback) => err.response?.data?.error || fallback;

const fmtDate = (d) => new Date(d).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });

// Hash routes: #/admin (list) and #/admin/scan/<id> (detail)
const parseRoute = () => {
  const m = window.location.hash.match(/#\/admin\/scan\/([0-9a-f-]{36})/i);
  return m ? { view: "detail", id: m[1] } : { view: "list" };
};

export default function AdminApp() {
  const [authed, setAuthed] = useState(null); // null = checking
  const [route, setRoute] = useState(parseRoute());
  const [industries, setIndustries] = useState([]);

  useEffect(() => {
    const onHash = () => setRoute(parseRoute());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  useEffect(() => {
    if (!getToken()) return setAuthed(false);
    adminApi.get("/me").then(() => setAuthed(true)).catch(() => { setToken(null); setAuthed(false); });
  }, []);

  useEffect(() => {
    if (!authed) return;
    axios.get(`${process.env.REACT_APP_BE_BASE_URL}/questions/industries`)
      .then(r => setIndustries(r.data || []))
      .catch(() => {});
  }, [authed]);

  // Any 401 from the API signs the admin out.
  useEffect(() => {
    const id = adminApi.interceptors.response.use(r => r, err => {
      if (err.response?.status === 401) { setToken(null); setAuthed(false); }
      return Promise.reject(err);
    });
    return () => adminApi.interceptors.response.eject(id);
  }, []);

  const industryLabel = useCallback(
    (key, other) => {
      if (!key) return "—";
      if (key === "other") return other ? `Other: ${other}` : "Other";
      return industries.find(i => i.key === key)?.label || key;
    },
    [industries]
  );

  const signOut = async () => {
    try { await adminApi.post("/logout"); } catch { /* already expired */ }
    setToken(null);
    setAuthed(false);
  };

  useEffect(() => { document.title = "Admin · Emeis AI Growth Scanner"; }, []);

  if (authed === null) return <div className="loader-screen"><Loader2 className="spin" size={28} color="#ff8a4c" /></div>;
  if (!authed) return <AdminLogin onSignedIn={() => setAuthed(true)} />;

  if (route.view === "detail") {
    return <ScanDetail id={route.id} industryLabel={industryLabel} onBack={() => { window.location.hash = "#/admin"; }} />;
  }
  return <ScansList industries={industries} industryLabel={industryLabel} onSignOut={signOut} />;
}

// -----------------------------
// LOGIN
// -----------------------------
function AdminLogin({ onSignedIn }) {
  const [stage, setStage] = useState("request"); // request | verify
  const [recipients, setRecipients] = useState([]);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [cooldown, setCooldown] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => {
    adminApi.get("/otp-target").then(r => setRecipients(r.data.recipients || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const requestCode = async () => {
    setBusy(true); setError(null); setInfo(null);
    try {
      const r = await adminApi.post("/request-otp");
      setStage("verify");
      setCooldown(60);
      setInfo(`Code sent to ${r.data.sentTo.join(", ")}. It expires in ${r.data.expiresInMinutes} minutes.`);
      setTimeout(() => inputRef.current?.focus(), 50);
    } catch (err) {
      setError(errMsg(err, "Could not send the code."));
      if (err.response?.status === 429) setStage("verify");
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e) => {
    e?.preventDefault();
    if (code.length !== 6) return setError("Enter the 6-digit code.");
    setBusy(true); setError(null);
    try {
      const r = await adminApi.post("/verify-otp", { code });
      setToken(r.data.token);
      onSignedIn();
    } catch (err) {
      setError(errMsg(err, "Verification failed."));
      setCode("");
      inputRef.current?.focus();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="success-screen">
      <div className="card success-card admin-login">
        <div><img src="./emeis.png" alt="Emeis Technologies" style={{ height: 30 }} /></div>
        <div style={{ marginTop: 20 }}><span className="pill"><ShieldCheck size={14} /> Admin access</span></div>
        <h1>Scans dashboard</h1>

        {stage === "request" ? (
          <>
            <p className="sub">
              We'll email a one-time sign-in code to{" "}
              <b>{recipients.length ? recipients.join(", ") : "the Emeis admin inbox"}</b>.
            </p>
            <button className="btn btn-primary btn-lg" style={{ width: "100%" }} onClick={requestCode} disabled={busy}>
              {busy ? <Loader2 size={18} className="spin" /> : <Mail size={18} />} Email me a sign-in code
            </button>
          </>
        ) : (
          <form onSubmit={verify}>
            <p className="sub">Enter the 6-digit code from the email.</p>
            <input
              ref={inputRef}
              className="input otp-input"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              placeholder="••••••"
              value={code}
              onChange={e => {
                const v = e.target.value.replace(/\D/g, "").slice(0, 6);
                setCode(v);
                setError(null);
              }}
            />
            <button type="submit" className="btn btn-primary btn-lg" style={{ width: "100%", marginTop: 14 }} disabled={busy || code.length !== 6}>
              {busy ? <Loader2 size={18} className="spin" /> : <KeyRound size={18} />} Verify & sign in
            </button>
            <button type="button" className="btn btn-ghost" style={{ width: "100%", marginTop: 10 }} onClick={requestCode} disabled={busy || cooldown > 0}>
              <RefreshCw size={16} /> {cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend code"}
            </button>
          </form>
        )}

        {info && !error && <div className="alert alert-info" style={{ textAlign: "left" }}><Mail size={18} /> {info}</div>}
        {error && <div className="alert alert-error" style={{ textAlign: "left" }}><AlertCircle size={18} /> {error}</div>}
      </div>
    </div>
  );
}

// -----------------------------
// LIST
// -----------------------------
const LEAD_BADGE = {
  hot: { label: "Hot", cls: "lead-hot" },
  warm: { label: "Warm", cls: "lead-warm" },
  cold: { label: "Cold", cls: "lead-cold" }
};

const daysLeft = (purgeAt) => Math.max(0, Math.ceil((new Date(purgeAt) - Date.now()) / 86400000));

// Notices can carry an Undo action (restore a just-deleted scan). They survive a
// detail → list navigation via sessionStorage.
const takeStoredNotice = () => {
  try {
    const raw = sessionStorage.getItem("admin-notice");
    sessionStorage.removeItem("admin-notice");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};
const storeNotice = (notice) => {
  try { sessionStorage.setItem("admin-notice", JSON.stringify(notice)); } catch { /* ignore */ }
};

function ScansList({ industries, industryLabel, onSignOut }) {
  const [view, setView] = useState("active"); // active | trash
  const [filters, setFilters] = useState({ search: "", industry: "", lead: "", page: 1 });
  const [searchInput, setSearchInput] = useState("");
  const [data, setData] = useState({ rows: [], total: 0, pageSize: 20 });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [confirm, setConfirm] = useState(null); // { scan, mode: "soft" | "permanent" }
  const [busyId, setBusyId] = useState(null);
  const [notice, setNotice] = useState(takeStoredNotice);

  useEffect(() => {
    if (!notice) return;
    const t = setTimeout(() => setNotice(null), notice.undoId ? 8000 : 4000);
    return () => clearTimeout(t);
  }, [notice]);

  // Debounce search typing
  useEffect(() => {
    const t = setTimeout(() => setFilters(f => (f.search === searchInput ? f : { ...f, search: searchInput, page: 1 })), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const params = useMemo(() => {
    const p = { page: filters.page, pageSize: 20 };
    if (filters.search) p.search = filters.search;
    if (filters.industry) p.industry = filters.industry;
    if (filters.lead) p.lead = filters.lead;
    if (view === "trash") p.trash = 1;
    return p;
  }, [filters, view]);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    Promise.all([adminApi.get("/scans", { params }), adminApi.get("/stats")])
      .then(([scans, s]) => { setData(scans.data); setStats(s.data); })
      .catch(err => setError(errMsg(err, "Failed to load scans.")))
      .finally(() => setLoading(false));
  }, [params]);

  useEffect(load, [load]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const { page, pageSize, trash, ...rest } = params;
      const r = await adminApi.get("/scans.csv", { params: rest, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ai-scans-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError("Export failed.");
    } finally {
      setExporting(false);
    }
  };

  const pages = Math.max(1, Math.ceil(data.total / data.pageSize));
  const setFilter = (patch) => setFilters(f => ({ ...f, ...patch, page: patch.page || 1 }));

  const switchView = (v) => {
    if (v === view) return;
    setView(v);
    setFilters(f => ({ ...f, page: 1 }));
  };

  // Reload, stepping back a page if the last row on it just disappeared.
  const reloadAfterRemoval = () => {
    if (data.rows.length === 1 && filters.page > 1) setFilter({ page: filters.page - 1 });
    else load();
  };

  const restore = async (scan) => {
    setBusyId(scan.id);
    setError(null);
    try {
      await adminApi.post(`/scans/${scan.id}/restore`);
      setNotice({ text: `Restored scan for ${scan.company_name || "unnamed company"}.`, tone: "success" });
      reloadAfterRemoval();
    } catch (err) {
      setError(errMsg(err, "Could not restore this scan."));
    } finally {
      setBusyId(null);
    }
  };

  const undoDelete = async () => {
    const id = notice?.undoId;
    if (!id) return;
    setNotice(null);
    try {
      await adminApi.post(`/scans/${id}/restore`);
      setNotice({ text: "Deletion undone. The scan is back in the list.", tone: "success" });
      load();
    } catch (err) {
      setError(errMsg(err, "Could not undo."));
    }
  };

  const isTrash = view === "trash";
  const retention = stats?.retentionDays || 30;

  return (
    <div className="page">
      <div className="report-toolbar">
        <div className="container-wide inner">
          <img src="./emeis.png" alt="Emeis Technologies" className="logo" />
          <span className="admin-title">Scans dashboard</span>
          <div className="spacer" />
          <button className="btn btn-ghost" onClick={exportCsv} disabled={exporting || isTrash} title="Export CSV">
            {exporting ? <Loader2 size={16} className="spin" /> : <Download size={16} />} <span>Export CSV</span>
          </button>
          <button className="btn btn-ghost" onClick={onSignOut} title="Sign out">
            <LogOut size={16} /> <span>Sign out</span>
          </button>
        </div>
      </div>

      <div className="container-wide" style={{ paddingTop: 24 }}>
        {stats && (
          <div className="kpi-grid admin-stats">
            <Stat icon={Users} label="Total scans" value={stats.total} tone="navy" />
            <Stat icon={Sparkles} label="Last 7 days" value={stats.last7} tone="teal" />
            <Stat icon={Flame} label="Hot / Warm / Cold" value={`${stats.hot} / ${stats.warm} / ${stats.cold}`} tone="orange" />
            <Stat icon={ShieldCheck} label="Average AI score" value={stats.avg_score ?? "–"} tone="green" />
          </div>
        )}

        <div className="card admin-card">
          <div className="admin-tabs" role="tablist">
            <button role="tab" aria-selected={!isTrash} className={`admin-tab ${!isTrash ? "active" : ""}`} onClick={() => switchView("active")}>
              <Users size={15} /> All scans {stats && <span className="tab-count">{stats.total}</span>}
            </button>
            <button role="tab" aria-selected={isTrash} className={`admin-tab ${isTrash ? "active" : ""}`} onClick={() => switchView("trash")}>
              <Trash2 size={15} /> Recently deleted {stats?.trash > 0 && <span className="tab-count">{stats.trash}</span>}
            </button>
          </div>

          {isTrash && (
            <div className="alert alert-info" style={{ marginTop: 0, marginBottom: 16 }}>
              <Clock3 size={18} /> Deleted scans are kept here for {retention} days, then removed permanently. Restore a scan to move it back to All scans.
            </div>
          )}

          <div className="admin-filters">
            <div className="search-box" style={{ margin: 0, flex: "1 1 260px" }}>
              <Search size={17} />
              <input
                className="input"
                placeholder="Search company, name, email or mobile"
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
              />
            </div>
            <select className="input admin-select" value={filters.industry} onChange={e => setFilter({ industry: e.target.value })}>
              <option value="">All industries</option>
              {industries.map(i => <option key={i.key} value={i.key}>{i.label}</option>)}
            </select>
            <select className="input admin-select" value={filters.lead} onChange={e => setFilter({ lead: e.target.value })}>
              <option value="">All leads</option>
              <option value="hot">Hot</option>
              <option value="warm">Warm</option>
              <option value="cold">Cold</option>
            </select>
          </div>

          {error && <div className="alert alert-error"><AlertCircle size={18} /> {error}</div>}
          {notice && (
            <div className={`alert ${notice.tone === "success" ? "alert-success" : "alert-neutral"}`}>
              {notice.tone === "success" ? <RotateCcw size={18} /> : <Trash2 size={18} />}
              <span style={{ flex: 1 }}>{notice.text}</span>
              {notice.undoId && <button className="link-btn" onClick={undoDelete}>Undo</button>}
            </div>
          )}

          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>{isTrash ? "Deleted" : "Date"}</th>
                  <th>Company</th>
                  <th>Contact</th>
                  <th>Industry</th>
                  <th className="num">Score</th>
                  {isTrash ? <th>Removed in</th> : <th>Lead</th>}
                  {!isTrash && <th className="num">Annual value</th>}
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {data.rows.map(r => {
                  const lead = LEAD_BADGE[r.lead_score];
                  const left = isTrash ? daysLeft(r.purge_at) : null;
                  return (
                    <tr key={r.id} onClick={() => { window.location.hash = `#/admin/scan/${r.id}`; }}>
                      <td className="muted nowrap">{fmtDate(isTrash ? r.deleted_at : r.created_at)}</td>
                      <td>
                        <div className="strong">{r.company_name || "—"}</div>
                        {isTrash
                          ? <div className="muted small">Scanned {fmtDate(r.created_at)}</div>
                          : r.website && <div className="muted small">{r.website}</div>}
                      </td>
                      <td>
                        <div>{r.contact_name || "—"}</div>
                        <div className="muted small">{r.contact_email}{r.mobile ? ` · ${r.mobile}` : ""}</div>
                      </td>
                      <td>{industryLabel(r.industry, r.industry_other)}</td>
                      <td className="num">
                        {r.report_id ? <span className="score-pill">{r.ai_score}</span> : <span className="muted small">No report</span>}
                      </td>
                      {isTrash ? (
                        <td><span className={`lead-badge ${left <= 3 ? "lead-hot" : "lead-cold"}`}>{left === 0 ? "Today" : `${left} day${left === 1 ? "" : "s"}`}</span></td>
                      ) : (
                        <td>{lead ? <span className={`lead-badge ${lead.cls}`}>{lead.label}</span> : <span className="muted">—</span>}</td>
                      )}
                      {!isTrash && <td className="num strong">{r.roi_estimate ? formatINR(r.roi_estimate) : "—"}</td>}
                      <td className="row-actions" onClick={e => e.stopPropagation()}>
                        {isTrash ? (
                          <div className="row-actions-inner">
                            <button className="btn btn-ghost btn-sm" disabled={busyId === r.id} onClick={() => restore(r)}>
                              {busyId === r.id ? <Loader2 size={14} className="spin" /> : <RotateCcw size={14} />} Restore
                            </button>
                            <button
                              className="icon-btn danger"
                              title="Delete forever"
                              aria-label={`Delete forever: ${r.company_name || "unnamed company"}`}
                              onClick={() => setConfirm({ scan: r, mode: "permanent" })}
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        ) : (
                          <button
                            className="icon-btn danger"
                            title="Delete scan"
                            aria-label={`Delete scan for ${r.company_name || "unnamed company"}`}
                            onClick={() => setConfirm({ scan: r, mode: "soft" })}
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {!loading && data.rows.length === 0 && (
                  <tr className="empty">
                    <td colSpan={8}>
                      {isTrash
                        ? (filters.search || filters.industry || filters.lead ? "No deleted scans match these filters." : "Recently deleted is empty.")
                        : "No scans match these filters."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            {loading && <div className="admin-loading"><Loader2 className="spin" size={22} /></div>}
          </div>

          <div className="admin-pager">
            <span className="muted">
              {data.total ? `${(filters.page - 1) * data.pageSize + 1}–${Math.min(filters.page * data.pageSize, data.total)} of ${data.total}` : "0 scans"}
            </span>
            <div className="spacer" />
            <button className="btn btn-ghost" disabled={filters.page <= 1} onClick={() => setFilter({ page: filters.page - 1 })}>
              <ChevronLeft size={16} /> Prev
            </button>
            <button className="btn btn-ghost" disabled={filters.page >= pages} onClick={() => setFilter({ page: filters.page + 1 })}>
              Next <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>

      {confirm && (
        <ConfirmDeleteModal
          scan={confirm.scan}
          mode={confirm.mode}
          retentionDays={retention}
          onCancel={() => setConfirm(null)}
          onDeleted={(scan) => {
            setConfirm(null);
            const name = scan.company_name || "unnamed company";
            setNotice(confirm.mode === "soft"
              ? { text: `Moved ${name} to Recently deleted.`, undoId: scan.id }
              : { text: `Permanently deleted ${name}.` });
            reloadAfterRemoval();
          }}
        />
      )}
    </div>
  );
}

const TONES = {
  navy: ["#f1f5f9", "#0b1220"],
  teal: ["#ecfeff", "#0e7490"],
  orange: ["#fff4ec", "#d9570d"],
  green: ["#f0fdf4", "#16a34a"]
};

// -----------------------------
// DELETE CONFIRMATION
// -----------------------------
// mode "soft" moves the scan to Recently deleted; "permanent" removes it for good.
function ConfirmDeleteModal({ scan, mode = "soft", retentionDays = 30, onCancel, onDeleted }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const cancelRef = useRef(null);
  const permanent = mode === "permanent";

  useEffect(() => {
    cancelRef.current?.focus();
    const onKey = (e) => { if (e.key === "Escape" && !busy) onCancel(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [busy, onCancel]);

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await adminApi.delete(permanent ? `/scans/${scan.id}/permanent` : `/scans/${scan.id}`);
      onDeleted(scan);
    } catch (err) {
      setError(errMsg(err, "Could not delete this scan."));
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" onMouseDown={e => { if (e.target === e.currentTarget && !busy) onCancel(); }}>
      <div className="modal" role="alertdialog" aria-modal="true" aria-labelledby="del-title" aria-describedby="del-desc">
        <button className="modal-close" onClick={onCancel} disabled={busy} aria-label="Close"><X size={18} /></button>
        <div className={`modal-icon ${permanent ? "" : "soft"}`}><Trash2 size={22} /></div>
        <h2 id="del-title">{permanent ? "Delete forever?" : "Delete this scan?"}</h2>
        <p id="del-desc">
          {permanent
            ? "This permanently removes the scan, its questionnaire answers and its AI report. This can't be undone."
            : `It moves to Recently deleted, where you can restore it for ${retentionDays} days. After that it's removed permanently.`}
        </p>
        <div className="modal-summary">
          <div className="strong">{scan.company_name || "Unnamed company"}</div>
          <div className="muted small">
            {[scan.contact_name, scan.contact_email].filter(Boolean).join(" · ")}
            {scan.created_at ? ` · ${fmtDate(scan.created_at)}` : ""}
          </div>
        </div>
        {error && <div className="alert alert-error"><AlertCircle size={18} /> {error}</div>}
        <div className="modal-actions">
          <button ref={cancelRef} className="btn btn-ghost" onClick={onCancel} disabled={busy}>Cancel</button>
          <button className="btn btn-danger" onClick={confirm} disabled={busy}>
            {busy ? <Loader2 size={16} className="spin" /> : <Trash2 size={16} />} {permanent ? "Delete forever" : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

const Stat = ({ icon: Icon, label, value, tone }) => (
  <div className="kpi">
    <div className="ic" style={{ background: TONES[tone][0], color: TONES[tone][1] }}><Icon size={18} /></div>
    <div className="v">{value}</div>
    <div className="l">{label}</div>
  </div>
);

// -----------------------------
// DETAIL
// -----------------------------
function ScanDetail({ id, industryLabel, onBack }) {
  const [data, setData] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [error, setError] = useState(null);

  const loadScan = useCallback(() => adminApi.get(`/scans/${id}`)
    .then(r => setData(r.data))
    .catch(err => setError(errMsg(err, "Failed to load scan."))), [id]);

  useEffect(() => {
    window.scrollTo(0, 0);
    loadScan();
  }, [loadScan]);

  if (error) {
    return (
      <div className="loader-screen">
        <div className="loader-card">
          <p>{error}</p>
          <button className="btn btn-primary" onClick={onBack}><ArrowLeft size={16} /> All scans</button>
        </div>
      </div>
    );
  }
  if (!data) return <div className="loader-screen"><Loader2 className="spin" size={28} color="#ff8a4c" /></div>;

  const c = data.company;
  const inTrash = Boolean(c.deleted_at);
  const purgeAt = inTrash ? new Date(new Date(c.deleted_at).getTime() + 30 * 86400000) : null;

  const restore = async () => {
    setRestoring(true);
    try {
      await adminApi.post(`/scans/${c.id}/restore`);
      await loadScan();
    } catch (err) {
      setError(errMsg(err, "Could not restore this scan."));
    } finally {
      setRestoring(false);
    }
  };

  const deleteButton = (
    <>
      {inTrash && (
        <button className="btn btn-ghost" onClick={restore} disabled={restoring} title="Restore scan">
          {restoring ? <Loader2 size={16} className="spin" /> : <RotateCcw size={16} />} <span>Restore</span>
        </button>
      )}
      <button className="btn btn-ghost btn-danger-ghost" onClick={() => setConfirming(true)} title={inTrash ? "Delete forever" : "Delete scan"}>
        <Trash2 size={16} /> <span>{inTrash ? "Delete forever" : "Delete"}</span>
      </button>
    </>
  );

  const trashBanner = inTrash && (
    <div className="alert alert-neutral" style={{ margin: "0 0 16px" }}>
      <Trash2 size={18} />
      <span style={{ flex: 1 }}>
        This scan is in Recently deleted. It will be removed permanently on{" "}
        <b>{purgeAt.toLocaleDateString("en-IN", { dateStyle: "medium" })}</b> unless you restore it.
      </span>
      <button className="link-btn" onClick={restore} disabled={restoring}>Restore</button>
    </div>
  );

  const deleteModal = confirming && (
    <ConfirmDeleteModal
      scan={c}
      mode={inTrash ? "permanent" : "soft"}
      onCancel={() => setConfirming(false)}
      onDeleted={(scan) => {
        const name = scan.company_name || "unnamed company";
        storeNotice(inTrash
          ? { text: `Permanently deleted ${name}.` }
          : { text: `Moved ${name} to Recently deleted.`, undoId: scan.id });
        onBack();
      }}
    />
  );
  const company = {
    companyName: c.company_name,
    name: c.contact_name,
    email: c.contact_email,
    mobile: c.mobile,
    website: c.website,
    industry: c.industry,
    industryLabel: industryLabel(c.industry, c.industry_other),
    challenges: c.challenges
  };

  const phone = String(c.mobile || "").replace(/[^\d+]/g, "");
  const wa = phone.replace(/^\+/, "").length === 10 ? `91${phone.replace(/^\+/, "")}` : phone.replace(/^\+/, "");
  const website = c.website ? (/^https?:\/\//i.test(c.website) ? c.website : `https://${c.website}`) : null;

  const contactCard = (
    <section className="card report-section admin-contact">
      <div className="admin-contact-grid">
        <div><div className="muted small">Contact</div><div className="strong">{c.contact_name || "—"}</div></div>
        <div><div className="muted small">Email</div>{c.contact_email ? <a href={`mailto:${c.contact_email}`}>{c.contact_email}</a> : "—"}</div>
        <div><div className="muted small">Mobile</div>{phone ? <a href={`tel:${phone}`}>{c.mobile}</a> : "—"}</div>
        <div><div className="muted small">Industry</div><div>{company.industryLabel}</div></div>
        <div><div className="muted small">Website</div>{website ? <a href={website} target="_blank" rel="noreferrer">{c.website}</a> : "—"}</div>
        <div><div className="muted small">Scanned on</div><div>{fmtDate(c.created_at)}</div></div>
      </div>
      <div className="admin-contact-actions no-print">
        {c.contact_email && <a className="btn btn-primary" href={`mailto:${c.contact_email}?subject=${encodeURIComponent("Your AI Growth Report: next steps with Emeis")}`}><Mail size={16} /> Email</a>}
        {phone && <a className="btn btn-ghost" href={`tel:${phone}`}><Phone size={16} /> Call</a>}
        {wa && <a className="btn btn-ghost" href={`https://wa.me/${wa}`} target="_blank" rel="noreferrer"><MessageCircle size={16} /> WhatsApp</a>}
        {website && <a className="btn btn-ghost" href={website} target="_blank" rel="noreferrer"><Globe size={16} /> Website</a>}
      </div>
    </section>
  );

  // Group Q&A by section, keeping questionnaire order
  const groups = [];
  data.responses.forEach(r => {
    const name = r.section === "Industry Focus"
      ? `${company.industryLabel} Focus`
      : r.section || "Questionnaire";
    let g = groups.find(x => x.name === name);
    if (!g) groups.push((g = { name, items: [] }));
    g.items.push(r);
  });

  const qna = (
    <section className="card report-section">
      <h3 className="section-title"><span className="ic"><Sparkles size={17} /></span>Questionnaire answers ({data.responses.length})</h3>
      {groups.length === 0 && <p className="muted">No answers recorded.</p>}
      {groups.map(g => (
        <div key={g.name} className="qna-group">
          <div className="qna-title">{g.name}</div>
          <table className="qna-table">
            <tbody>
              {g.items.map((r, i) => (
                <tr key={i}><td className="q">{r.question}</td><td className="a">{r.answer}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </section>
  );

  if (!data.report) {
    return (
      <div className="page">
        <div className="report-toolbar">
          <div className="container-wide inner">
            <button className="btn btn-ghost" onClick={onBack}><ArrowLeft size={16} /> <span>All scans</span></button>
            <img src="./emeis.png" alt="Emeis Technologies" className="logo" />
            <div className="spacer" />
            {deleteButton}
          </div>
        </div>
        <div className="container-wide" style={{ paddingTop: 24 }}>
          <h1 style={{ margin: "0 0 4px" }}>{c.company_name || "Unnamed company"}</h1>
          {trashBanner}
          <div className="alert alert-info" style={{ marginBottom: 16 }}>
            <AlertCircle size={18} /> No report was generated for this scan (the AI step failed). Contact details and answers are below.
          </div>
          {contactCard}
          {c.challenges && (
            <section className="card report-section">
              <h3 className="section-title">Challenges they shared</h3>
              <p style={{ margin: 0, whiteSpace: "pre-line" }}>{c.challenges}</p>
            </section>
          )}
          {qna}
        </div>
        {deleteModal}
      </div>
    );
  }

  const report = mapAIReport(data.report);

  return (
    <>
    <ReportDashboard
      report={report}
      company={company}
      admin={{
        onBack,
        actions: deleteButton,
        date: c.created_at,
        top: (
          <>
            {trashBanner}
            {contactCard}
            {data.report.is_legacy && (
              <div className="alert alert-info" style={{ margin: "0 0 16px" }}>
                <AlertCircle size={18} /> This scan was made before the industry update, so some report sections aren't available.
              </div>
            )}
          </>
        ),
        bottom: qna
      }}
    />
    {deleteModal}
    </>
  );
}
